'use strict';

const MAX_PORTION_SIZE = 10;
const STATIC_PORTION_SIZE = 1;
const STATIC_PORTION_SIZE_UPPER_LIMIT_kCAL = 2400;
const STATIC_PORTION_SIZE_LOWER_LIMIT_kCAL = 1200;

import { isSingular } from '../utils/Math';

export const familyPortionOpts = [
    {label: '½ Serving',           value: 0.5},
    {label: 'Single Serving (1)',  value: 1},
    {label: '1¼ Serving',          value: 1.25},
    {label: 'Large Serving (1½)',  value: 1.5},
    {label: '1¾ Serving',          value: 1.75},
    {label: 'Double Serving (2)',  value: 2},
    {label: 'Triple Serving (3)',  value: 3},
];

export const getFamilyPortionOpts = (portion) => {
    if (familyPortionOpts.some(option => option.value === portion)) {
        return familyPortionOpts;
    }

    return [
        ...familyPortionOpts,
        {label: `${portion} ` + (isSingular(portion) ? 'Serving' : 'Servings'), value: portion},
    ].sort((a, b) => a.value - b.value);
};

export const childPortionOpts = [
    {label: '½ Serving', value: 0.5},
    {label: 'Single Serving (1)', value: 1},
];

export const getPortionSizes = (portionResolution, recommendedPortion = null) => {
    let total = 0.5;
    const portionSizes = [];
    while(total < MAX_PORTION_SIZE) {
        portionSizes.push({label: `${total} ` + (isSingular(total) ? 'Serving' : 'Servings'), value: total});
        total+=portionResolution;
    }

    return portionSizes.map(option => {
        if (!!recommendedPortion && option.value === recommendedPortion) {
            option.sub_label = 'Recommended';
        }
        return option;
    });
}

/**
 * Looks up the portion size from calorie-based brackets
 *
 * Takes a calorie value and an array of portion size brackets, and returns
 * the portion size for the bracket that contains the calorie value.
 *
 * Each bracket should have: min_kcal, max_kcal, portion
 * Brackets define ranges where min_kcal is inclusive and max_kcal is exclusive.
 *
 * Edge cases:
 * - If kcal is below the lowest bracket, uses the first bracket
 * - If kcal is at or above the highest bracket's max, uses the last bracket
 *
 * @param {number} kcal - The calorie value to look up
 * @param {Array<{min_kcal: number, max_kcal: number, portion: number}>} brackets - Array of portion brackets
 * @returns {number|null} The portion size from the matching bracket, or null if no brackets provided
 */
export const getPortionSizeFromBrackets = (kcal, brackets) => {
    // Handle null, undefined, or empty brackets
    if (!brackets || !Array.isArray(brackets) || brackets.length === 0) {
        return null;
    }

    // Sort brackets by min_kcal (defensive - should already be sorted from backend)
    const sortedBrackets = [...brackets].sort((a, b) => a.min_kcal - b.min_kcal);

    // Edge case: kcal is below the lowest bracket - use first bracket
    if (kcal < sortedBrackets[0].min_kcal) {
        return sortedBrackets[0].portion;
    }

    // Edge case: kcal is at or above the highest bracket's max - use last bracket
    const lastBracket = sortedBrackets[sortedBrackets.length - 1];
    if (kcal >= lastBracket.max_kcal) {
        return lastBracket.portion;
    }

    // Find the bracket where min_kcal <= kcal < max_kcal
    for (let i = 0; i < sortedBrackets.length; i++) {
        const bracket = sortedBrackets[i];
        if (kcal >= bracket.min_kcal && kcal < bracket.max_kcal) {
            return bracket.portion;
        }
    }

    // Fallback: should not reach here if brackets are continuous, but return null for safety
    return null;
}

/**
 * Calculates the nearest portion size based on calorie value
 *
 * First checks if portion brackets are provided - if so, uses bracket-based lookup.
 * If no brackets or no bracket match, falls back to the global portion calculation:
 * - Checks deadzone (1200-2400 kcal defaults to 1.0 portion)
 * - Calculates nearest portion based on ratio to standard calorie value
 *
 * @param {number} kcal - The calorie value
 * @param {number} portionResolution - The increment between available portion sizes (e.g., 0.25, 0.5)
 * @param {number} standardKcal - The standard calorie value for 1.0 portion (default: 2000)
 * @param {boolean} inhibitDeadzone - If true, bypasses the 1200-2400 kcal deadzone (default: false)
 * @param {Array<{min_kcal: number, max_kcal: number, portion: number}>|null} portionBrackets - Optional calorie-to-portion brackets
 * @returns {number} The calculated portion size
 */
export const calculateNearestPortionSize = (kcal, portionResolution, standardKcal = 2000, inhibitDeadzone = false, portionBrackets = null) => {
    // Check for bracket-based portion first
    if (portionBrackets) {
        const bracketPortion = getPortionSizeFromBrackets(kcal, portionBrackets);
        if (bracketPortion !== null) {
            return bracketPortion;
        }
    }

    // Fall back to global portion calculation logic
    if (inhibitDeadzone == false &&
        kcal >= STATIC_PORTION_SIZE_LOWER_LIMIT_kCAL &&
        kcal <= STATIC_PORTION_SIZE_UPPER_LIMIT_kCAL) {
        return STATIC_PORTION_SIZE;
    }

    const availablePortionSizes = getPortionSizes(portionResolution).map(data => data.value);
    const approxServingSize = kcal / standardKcal;
    let nearestPortionSize = availablePortionSizes[0];
    let diff = Math.abs(nearestPortionSize - approxServingSize);
    availablePortionSizes.forEach(portionSize => {
        if(Math.abs(approxServingSize-portionSize) < diff) {
            diff = Math.abs(approxServingSize-portionSize);
            nearestPortionSize = portionSize;
        }
    });
    return nearestPortionSize;
}

/**
 * Determines if an eating pattern is allowed for a given calorie value
 *
 * Checks if the user's target calories fall within a bracket marked as not_allowed.
 * Used to filter eating patterns from selection dropdowns based on calorie appropriateness.
 *
 * @param {Object} eatingPattern - The eating pattern object containing portion_size_brackets
 * @param {number} targetCalories - The user's target calorie value
 * @returns {boolean} True if the eating pattern is allowed for these calories, false if not allowed
 */
export const isEatingPatternAllowedForCalories = (eatingPattern, targetCalories) => {
    // If pattern has no brackets, it's always allowed (uses global calculation)
    const brackets = eatingPattern?.portion_size_brackets;
    if (!brackets || !Array.isArray(brackets) || brackets.length === 0) {
        return true;
    }

    // Find the bracket that matches the target calories
    const matchingBracket = brackets.find(bracket =>
        targetCalories >= bracket.min_kcal && targetCalories < bracket.max_kcal
    );

    // If no matching bracket found, pattern is allowed (edge case/fallback)
    if (!matchingBracket) {
        return true;
    }

    // If matching bracket has not_allowed=true, pattern is not allowed
    if (matchingBracket.not_allowed === true) {
        return false;
    }

    // Otherwise, pattern is allowed
    return true;
}
